import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useTasksData } from '~/backend/connector-next-api/connector-next-components';
import { TaskUidPayload } from '~/backend/connector-next-api/connector-next-schemas';
import { WorkflowTargetIds } from '~/hooks/workflows/useWorkflowTargetIds';
import {
    NewJobData,
    useJobManagement,
} from '~/hooks/workflows/useJobManagement';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import useCheckTaskInputs, {
    type CheckedTaskInputs,
} from '../useCheckTaskInputs';
import useUploadDoseForTask from '../useUploadDoseForTask';
import { handleTaskStartError, requireTaskId } from '../utils';

type UseDataUploadTaskProps = {
    environmentObjectIds?: WorkflowTargetIds;
    patientId: string;
};

/**
 * Transforms environmentObjectIds into a list of task UID payloads.
 * @param storedStructureSetUid - The SOP Instance UID the patient database
 *   stores for the structure set, given the one the session knows it by.
 * @returns {TaskUidPayload[]} The list of upload items.
 */
const transformEnvironmentToUploadItems = (
    environmentObjectIds: WorkflowTargetIds,
    patientID: string,
    storedStructureSetUid: CheckedTaskInputs['storedStructureSetUid']
): TaskUidPayload[] => {
    if (!environmentObjectIds) {
        return [];
    }
    const uploadItems: TaskUidPayload[] = [];

    if (environmentObjectIds.primaryImage) {
        uploadItems.push({ ...environmentObjectIds.primaryImage, patientID });
    }

    if (environmentObjectIds.secondaryImage) {
        uploadItems.push({ ...environmentObjectIds.secondaryImage, patientID });
    }

    if (environmentObjectIds.structureSet) {
        const { sopInstanceUID } = environmentObjectIds.structureSet;
        uploadItems.push({
            sopInstanceUID:
                sopInstanceUID && storedStructureSetUid(sopInstanceUID),
            patientID,
        });
    }

    if (environmentObjectIds.dose) {
        uploadItems.push({
            sopInstanceUID: environmentObjectIds.dose.sopInstanceUID,
            patientID,
        });
    }

    return uploadItems;
};

/** The UIDs that are set, in order. */
const definedUids = (...uids: (string | undefined)[]): string[] =>
    uids.filter((uid): uid is string => !!uid);

/**
 * Handles starting data upload tasks and monitoring their progress.
 * @param {UseDataUploadTaskProps} props - Contains environment object IDs and patient ID for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useDataUploadTask({
    environmentObjectIds,
    patientId,
}: UseDataUploadTaskProps) {
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startUploading } = useTasksData();
    const checkTaskInputs = useCheckTaskInputs();
    const uploadDose = useUploadDoseForTask();
    const { addJob } = useJobManagement();

    /**
     * Starts the data upload task with the provided environmentObjectIds.
     * @param {string} action - The action header for the upload task.
     */
    const startUpload = async (action: string) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (!environmentObjectIds) {
                handleError('No active environment object available.');
                return;
            }
            const inputs = await checkTaskInputs({
                patientId,
                seriesInstanceUids: definedUids(
                    environmentObjectIds.primaryImage?.seriesInstanceUID,
                    environmentObjectIds.secondaryImage?.seriesInstanceUID
                ),
                structureSetSopInstanceUids: definedUids(
                    environmentObjectIds.structureSet?.sopInstanceUID
                ),
            });
            // The save workflow has no dose upload, so the viewer's dose is
            // imported here; a re-sent dose replaces the stored copy.
            if (environmentObjectIds?.dose?.sopInstanceUID) {
                await uploadDose();
            }

            const uploadItems = transformEnvironmentToUploadItems(
                environmentObjectIds,
                patientId,
                inputs.storedStructureSetUid
            );

            const task = await startUploading({
                body: {
                    modelName: action,
                    uploadItems,
                },
            });

            const newJob: NewJobData = {
                name: `Data Upload+`,
                id: requireTaskId(task),
                type: WorkflowTypes.DATA_UPLOAD,
                task: {
                    itemsAmount: uploadItems.length,
                    output: task,
                },
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleTaskStartError(error, 'Failed to start data upload task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startUpload, isSubmitting };
}
